// Draft storage utilities for auto-saving form data and comments

const TICKET_DRAFT_KEY = (step: number) => `ticket-draft-step-${step}`;
const COMMENT_DRAFT_KEY = (ticketId: number) => `comment-draft-${ticketId}`;
const TICKET_DRAFT_TIMESTAMP_KEY = 'ticket-draft-timestamp';

export interface TicketDraftData {
  step: number;
  data: Record<string, any>;
  timestamp: number;
}

export interface CommentDraftData {
  body: string;
  isInternal: boolean;
  timestamp: number;
}

// Ticket Draft Functions
export const saveTicketDraft = (step: number, data: Record<string, any>) => {
  try {
    const draftData: TicketDraftData = {
      step,
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(TICKET_DRAFT_KEY(step), JSON.stringify(draftData));
    localStorage.setItem(TICKET_DRAFT_TIMESTAMP_KEY, JSON.stringify(Date.now()));
  } catch (error) {
    console.error('Error saving ticket draft:', error);
  }
};

export const getTicketDraft = (step: number): TicketDraftData | null => {
  try {
    const draft = localStorage.getItem(TICKET_DRAFT_KEY(step));
    return draft ? JSON.parse(draft) : null;
  } catch (error) {
    console.error('Error reading ticket draft:', error);
    return null;
  }
};

export const clearTicketDraft = (step?: number) => {
  try {
    if (step !== undefined) {
      localStorage.removeItem(TICKET_DRAFT_KEY(step));
    } else {
      // Clear all steps (1-3)
      for (let i = 1; i <= 3; i++) {
        localStorage.removeItem(TICKET_DRAFT_KEY(i));
      }
      localStorage.removeItem(TICKET_DRAFT_TIMESTAMP_KEY);
    }
  } catch (error) {
    console.error('Error clearing ticket draft:', error);
  }
};

export const hasTicketDraft = (step: number): boolean => {
  try {
    return localStorage.getItem(TICKET_DRAFT_KEY(step)) !== null;
  } catch {
    return false;
  }
};

export const getTicketDraftTimestamp = (): Date | null => {
  try {
    const timestamp = localStorage.getItem(TICKET_DRAFT_TIMESTAMP_KEY);
    return timestamp ? new Date(JSON.parse(timestamp)) : null;
  } catch {
    return null;
  }
};

// Comment Draft Functions
export const saveCommentDraft = (ticketId: number, body: string, isInternal: boolean = false) => {
  try {
    const draftData: CommentDraftData = {
      body,
      isInternal,
      timestamp: Date.now(),
    };
    localStorage.setItem(COMMENT_DRAFT_KEY(ticketId), JSON.stringify(draftData));
  } catch (error) {
    console.error('Error saving comment draft:', error);
  }
};

export const getCommentDraft = (ticketId: number): CommentDraftData | null => {
  try {
    const draft = localStorage.getItem(COMMENT_DRAFT_KEY(ticketId));
    return draft ? JSON.parse(draft) : null;
  } catch (error) {
    console.error('Error reading comment draft:', error);
    return null;
  }
};

export const clearCommentDraft = (ticketId: number) => {
  try {
    localStorage.removeItem(COMMENT_DRAFT_KEY(ticketId));
  } catch (error) {
    console.error('Error clearing comment draft:', error);
  }
};

export const hasCommentDraft = (ticketId: number): boolean => {
  try {
    return localStorage.getItem(COMMENT_DRAFT_KEY(ticketId)) !== null;
  } catch {
    return false;
  }
};

// Helper function for debouncing
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};
